import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider } from "./context/AuthContext";
import { YachtProvider } from "./context/YachtContext";
import { Toaster } from "./components/ui/sonner";
import ProtectedRoute from "./components/ProtectedRoute";
import AppShell from "./components/AppShell";
import GlobalErrorOverlay from "./components/GlobalErrorOverlay";

import Landing from "./pages/marketing/Landing";
import Features from "./pages/marketing/Features";
import RolePage from "./pages/marketing/RolePage";
import RequestAccess from "./pages/marketing/RequestAccess";
import SupplierApply from "./pages/SupplierApply";

import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import VerifyEmail from "./pages/auth/VerifyEmail";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";

import DeleteApprove from "./pages/DeleteApprove";
import CrewRemoveApprove from "./pages/CrewRemoveApprove";
import AccessApprove from "./pages/admin/AccessApprove";
import ConfirmJobLanding from "./pages/ConfirmJobLanding";

import Dashboard from "./pages/app/Dashboard";
import Yachts from "./pages/app/Yachts";
import Inventory from "./pages/app/Inventory";
import Maintenance from "./pages/app/Maintenance";
import Tasks from "./pages/app/Tasks";
import TaskCalendar from "./pages/app/TaskCalendar";
import Messages from "./pages/app/Messages";
import Checklists from "./pages/app/Checklists";
import Logbook from "./pages/app/Logbook";
import PettyCash from "./pages/app/PettyCash";
import Invoices from "./pages/app/Invoices";
import Documents from "./pages/app/Documents";
import Photos from "./pages/app/Photos";
import Reports from "./pages/app/Reports";
import CrewNetwork from "./pages/app/CrewNetwork";
import CrewProfile from "./pages/app/CrewProfile";
import CrewHR from "./pages/app/CrewHR";
import CrewDocsOverview from "./pages/app/CrewDocsOverview";
import MyHR from "./pages/app/MyHR";
import TalentPool from "./pages/app/TalentPool";
import Marketplace from "./pages/app/Marketplace";
import MarketplaceAdmin from "./pages/app/MarketplaceAdmin";
import PlatformAdmin from "./pages/app/PlatformAdmin";
import SupplierDashboard from "./pages/app/SupplierDashboard";
import SupplierTasks from "./pages/app/SupplierTasks";
import SupplierInvoices from "./pages/app/SupplierInvoices";
import SupplierTeam from "./pages/app/SupplierTeam";
import SupplierInviteAccept from "./pages/SupplierInviteAccept";
import ReferralRedirect from "./pages/ReferralRedirect";
import AffiliateLanding from "./pages/affiliate/AffiliateLanding";
import AffiliateLogin from "./pages/affiliate/AffiliateLogin";
import AffiliateVerify from "./pages/affiliate/AffiliateVerify";
import AffiliateDashboard from "./pages/affiliate/AffiliateDashboard";
import { AffiliateForgot, AffiliateReset } from "./pages/affiliate/AffiliatePassword";
import Help from "./pages/app/Help";
import { TourProvider } from "./components/tour/TourContext";

import "./App.css";

import { useYacht } from "./context/YachtContext";
import { useAuth } from "./context/AuthContext";

/**
 * Picks the right "home" view inside the app shell:
 *  - while yachts are still loading -> show Dashboard (it has its own skeleton)
 *  - if the user has zero yachts     -> redirect to /app/yachts (so they create one)
 *  - otherwise                       -> show Dashboard
 */
function AppIndex() {
  const { yachts, loading } = useYacht();
  const { user } = useAuth();
  if (loading) return <Dashboard />;
  if (!yachts || yachts.length === 0) {
    // A marketplace supplier / provider account has no yachts — send them to
    // their hub instead of the crew-oriented "create a yacht" screen.
    // `is_supplier` only arrives via /auth/me, so also key off account_kind
    // (present immediately in the login response) to avoid a flash of /yachts.
    if (user?.is_supplier || user?.account_kind === "provider") return <Navigate to="/app/supplier" replace />;
    return <Navigate to="/app/yachts" replace />;
  }
  return <Dashboard />;
}

function AppRouter() {
  // Drop a breadcrumb on every route change so when the user reports a
  // mid-navigation error we can read the last URL they were on right
  // before it fired (Android Firefox / Samsung Fold report "checklists
  // menu errored" type symptoms that are otherwise invisible without
  // this trace).
  const loc = useLocation();
  useEffect(() => {
    try {
      window.__seavantoDebug &&
        window.__seavantoDebug.log(`route: ${loc.pathname}${loc.search || ""}`);
    } catch { /* ignore — debug helper may not be mounted yet */ }
  }, [loc.pathname, loc.search]);

  return (
    <Routes>
      {/* Marketing */}
      <Route path="/" element={<Landing />} />
      <Route path="/features" element={<Features />} />
      {/* Beta-lock: /pricing and /register hidden until the platform leaves
          private beta. They redirect to the marketing landing page so any
          stray inbound link still resolves to something useful. */}
      <Route path="/pricing" element={<Navigate to="/" replace />} />
      <Route path="/for-owners" element={<RolePage role="owners" />} />
      <Route path="/for-captains" element={<RolePage role="captains" />} />
      <Route path="/for-crew" element={<RolePage role="crew" />} />
      <Route path="/for-providers" element={<RolePage role="providers" />} />
      <Route path="/request-access" element={<RequestAccess />} />
      <Route path="/supplier/apply" element={<SupplierApply />} />
      <Route path="/supplier-invite" element={<SupplierInviteAccept />} />
      {/* Affiliate program */}
      <Route path="/affiliate" element={<AffiliateLanding />} />
      <Route path="/affiliate/login" element={<AffiliateLogin />} />
      <Route path="/affiliate/verify" element={<AffiliateVerify />} />
      <Route path="/affiliate/forgot-password" element={<AffiliateForgot />} />
      <Route path="/affiliate/reset-password" element={<AffiliateReset />} />
      <Route path="/affiliate/dashboard" element={<AffiliateDashboard />} />
      <Route path="/r/:code" element={<ReferralRedirect />} />

      {/* Auth */}
      <Route path="/login" element={<Login />} />
      {/* Register stays reachable for supplier claim links; the backend
          enforces the private-beta lock for everyone else. */}
      <Route path="/register" element={<Register />} />
      <Route path="/auth/callback" element={<Navigate to="/login" replace />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Public yacht-deletion approval (token-authenticated) */}
      <Route path="/yacht/delete-approve" element={<DeleteApprove />} />
      <Route path="/crew/remove-approve" element={<CrewRemoveApprove />} />
      <Route path="/admin/access-approve" element={<AccessApprove />} />
      {/* Magic-link "Confirm job" landing — the backend redirects here
          with a ?status= query param after consuming the token. */}
      <Route path="/confirm-job" element={<ConfirmJobLanding />} />

      {/* App (protected) */}
      <Route
        path="/app/*"
        element={
          <ProtectedRoute>
            <YachtProvider>
              <TourProvider>
              <AppShell>
                <Routes>
                  <Route index element={<AppIndex />} />
                  <Route path="yachts" element={<Yachts />} />
                  <Route path="inventory" element={<Inventory />} />
                  <Route path="maintenance" element={<Maintenance restrictTo="routine" pageTitle="Periodic tasks" />} />
                  <Route path="tasks" element={<Tasks />} />
                  <Route path="calendar" element={<TaskCalendar />} />
                  <Route path="messages" element={<Messages />} />
                  <Route path="checklists" element={<Checklists />} />
                  <Route path="logbook" element={<Logbook />} />
                  <Route path="petty-cash" element={<PettyCash />} />
                  <Route path="invoices" element={<Invoices />} />
                  <Route path="documents" element={<Documents />} />
                  <Route path="photos" element={<Photos />} />
                  <Route path="reports" element={<Reports />} />
                  <Route path="users" element={<Navigate to="/app/crew" replace />} />
                  <Route path="network" element={<Navigate to="/app/crew?view=network" replace />} />
                  <Route path="crew" element={<CrewNetwork />} />
                  <Route path="profile" element={<CrewProfile />} />
                  <Route path="talent" element={<TalentPool />} />
                  <Route path="marketplace" element={<Marketplace />} />
                  <Route path="marketplace/admin" element={<MarketplaceAdmin />} />
        <Route path="platform-admin" element={<PlatformAdmin />} />
                  <Route path="supplier" element={<SupplierDashboard />} />
                  <Route path="supplier/tasks" element={<SupplierTasks />} />
                  <Route path="supplier/invoices" element={<SupplierInvoices />} />
                  <Route path="supplier/team" element={<SupplierTeam />} />
                  <Route path="crew-hr/:userId" element={<CrewHR />} />
                  <Route path="crew-docs" element={<CrewDocsOverview />} />
                  <Route path="my-hr" element={<MyHR />} />
                  <Route path="settings" element={<Navigate to="/app/profile" replace />} />
                  <Route path="help" element={<Help />} />
                  <Route path="dashboard" element={<Navigate to="/app" replace />} />
                  <Route path="*" element={<Navigate to="/app" replace />} />
                </Routes>
              </AppShell>
              </TourProvider>
            </YachtProvider>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <GlobalErrorOverlay />
          <AppRouter />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
